import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, TrendingUp, Shield, Clock, DollarSign, Building2, Wrench, RefreshCw } from 'lucide-react';

const programs = [
  {
    id: 'sba',
    label: 'SBA 7(a)',
    tagline: 'Government Backed, Low Rates',
    color: 'teal',
    icon: <Shield className="h-6 w-6" />,
    hero: {
      title: 'SBA 7(a) Working Capital Loan',
      subtitle: 'The gold standard of small business lending, backed by the U.S. government with some of the lowest rates available.',
      image: 'https://images.pexels.com/photos/425122/pexels-photo-425122.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    },
    highlights: [
      { value: 'Up to $350K', label: 'Loan Amount' },
      { value: '10 Year', label: 'Term' },
      { value: 'Prime + 2.75%', label: 'Starting Rate' },
      { value: '600+', label: 'FICO Score' },
    ],
    details: [
      { label: 'Loan Amount', value: 'Up to $350,000' },
      { label: 'Term', value: '10 years' },
      { label: 'Interest Rate', value: 'WSJ Prime + 2.75% to 3.75%' },
      { label: 'FICO Score', value: '600+' },
      { label: 'Time in Business', value: '2+ years' },
      { label: 'Annual Revenue', value: '$100,000+' },
    ],
    benefits: [
      'Among the lowest interest rates available for small business loans',
      'Long 10-year repayment term keeps monthly payments manageable',
      'Government backing means more favorable terms for you',
      'Can be used for working capital, expansion, and more',
    ],
  },
  {
    id: 'growth',
    label: '10 Year Growth',
    tagline: 'No SBA Required',
    color: 'blue',
    icon: <TrendingUp className="h-6 w-6" />,
    hero: {
      title: '10 Year Growth Term Loan',
      subtitle: 'The same long term stability without the SBA process. Faster approvals, broader eligibility, and a decade to repay.',
      image: 'https://images.pexels.com/photos/407312/pexels-photo-407312.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    },
    highlights: [
      { value: '$25K to $200K', label: 'Loan Amount' },
      { value: '10 Year', label: 'Term' },
      { value: '15 to 24%', label: 'Target Rate' },
      { value: '640+', label: 'FICO Score' },
    ],
    details: [
      { label: 'Loan Amount', value: '$25,000 to $200,000' },
      { label: 'Term', value: '10 years' },
      { label: 'Target Rate', value: '15 to 24% (varies by risk profile)' },
      { label: 'FICO Score', value: '640+ for all owners (20%+)' },
      { label: 'Time in Business', value: '2+ years' },
      { label: 'Annual Revenue', value: '$100,000+' },
    ],
    benefits: [
      'No SBA approval needed, faster, simpler process',
      '10 year term provides long term payment predictability',
      'Refinance MCA debt into stable, long term payments',
      'Only 51% U.S. citizen or permanent resident ownership required',
      'Positive EBITDA focus, we look at your profitability, not just your score',
    ],
  },
  {
    id: 'shortterm',
    label: '3 to 5 Year',
    tagline: 'Refinance & Working Capital',
    color: 'purple',
    icon: <RefreshCw className="h-6 w-6" />,
    hero: {
      title: '3 to 5 Year Business Term Loan',
      subtitle: 'Purpose built for refinancing expensive debt and fueling growth. No collateral required beyond a standard UCC filing.',
      image: 'https://images.pexels.com/photos/7256463/pexels-photo-7256463.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
    },
    highlights: [
      { value: 'Up to $300K', label: 'Loan Amount' },
      { value: '3 to 5 Years', label: 'Term' },
      { value: '~18% Avg', label: 'Interest Rate' },
      { value: '680+', label: 'FICO Score' },
    ],
    details: [
      { label: 'Loan Amount', value: 'Up to $300,000' },
      { label: 'Term', value: '36 to 60 months' },
      { label: 'Interest Rate', value: 'Starting at 11.99%, avg. ~18%' },
      { label: 'FICO Score', value: '680+' },
      { label: 'Time in Business', value: '3+ years' },
      { label: 'Prepayment Penalty', value: 'None' },
    ],
    benefits: [
      'Refinance MCA and other expensive debt into predictable payments',
      'No collateral required, just a standard blanket UCC filing',
      'No prepayment penalty, pay it off early with no extra cost',
      'All industries eligible, B2B and B2C',
      'Use funds for working capital, hiring, marketing, equipment, or inventory',
      'Cashflow based underwriting, no equity or leverage requirements',
    ],
  },
];

const colorMap = {
  teal: { highlight: 'text-teal-300', tab: 'bg-teal-600 text-white', button: 'bg-teal-600 hover:bg-teal-500', checkColor: 'text-teal-400', gradientFrom: 'from-teal-950' },
  blue: { highlight: 'text-blue-300', tab: 'bg-blue-600 text-white', button: 'bg-blue-600 hover:bg-blue-500', checkColor: 'text-blue-400', gradientFrom: 'from-blue-950' },
  purple: { highlight: 'text-purple-300', tab: 'bg-purple-600 text-white', button: 'bg-purple-600 hover:bg-purple-500', checkColor: 'text-purple-400', gradientFrom: 'from-purple-950' },
};

const TermLoans = () => {
  const [activeTab, setActiveTab] = useState('sba');
  const program = programs.find((p) => p.id === activeTab);
  const colors = colorMap[program.color];

  return (
    <div className="bg-[#FAFAFA]">
      {/* Hero with background image */}
      <section className="relative min-h-[70vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={program.hero.image} alt="" className="w-full h-full object-cover transition-opacity duration-500" />
          <div className={`absolute inset-0 bg-gradient-to-br ${colors.gradientFrom} via-slate-900/90 to-slate-900/80`} />
        </div>
        <div className="absolute inset-0 noise-overlay" />

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-8 w-full py-28">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/10 rounded-full px-4 py-1.5 mb-8">
                <TrendingUp className="h-4 w-4 text-teal-300" />
                <span className="text-xs font-semibold text-slate-200 uppercase tracking-wider">Term Loans</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.08] mb-6" style={{ fontFamily: 'var(--font-heading)' }}>
                Three Programs,<br />
                <span className={colors.highlight}>One Goal</span>
              </h1>
              <p className="text-lg text-slate-300 leading-relaxed mb-10 max-w-md">
                From government backed SBA loans to flexible short term financing, find the right term loan for your business stage and goals. Cashflow Engineering at its core.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/apply" data-testid="term-loans-hero-apply" className="inline-flex items-center gap-2 bg-white text-slate-900 font-semibold text-sm px-7 py-3.5 rounded-full hover:bg-slate-100 transition-all">
                  Apply Now <ArrowRight className="h-4 w-4" />
                </Link>
                <Link to="/contact" className="inline-flex items-center gap-2 border border-white/20 text-white font-semibold text-sm px-7 py-3.5 rounded-full hover:bg-white/10 transition-all">
                  Talk to Us
                </Link>
              </div>
            </div>

            <div className="hidden lg:flex flex-col gap-3">
              {programs.map((p) => {
                const isActive = p.id === activeTab;
                return (
                  <button key={p.id} data-testid={`tab-${p.id}`} onClick={() => setActiveTab(p.id)}
                    className={`text-left rounded-xl p-5 border transition-all duration-300 ${isActive ? 'bg-white/10 border-white/20 shadow-lg' : 'bg-white/5 border-white/5 hover:bg-white/8 hover:border-white/10'}`}>
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isActive ? 'bg-white/20' : 'bg-white/10'}`}>
                        <span className={isActive ? colors.highlight : 'text-slate-400'}>{p.icon}</span>
                      </div>
                      <div>
                        <div className="text-sm font-bold text-white">{p.label}</div>
                        <div className="text-xs text-slate-400">{p.tagline}</div>
                      </div>
                      {isActive && <ArrowRight className="h-4 w-4 text-white ml-auto" />}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Mobile tabs */}
      <div className="lg:hidden sticky top-20 z-40 bg-white border-b border-slate-200 px-4 py-3">
        <div className="flex gap-2 overflow-x-auto">
          {programs.map((p) => (
            <button key={p.id} onClick={() => setActiveTab(p.id)}
              className={`whitespace-nowrap text-xs font-bold px-4 py-2 rounded-full transition-colors ${p.id === activeTab ? colorMap[p.color].tab : 'bg-slate-100 text-slate-500'}`}>
              {p.label}
            </button>
          ))}
        </div>
      </div>

      {/* Program Detail */}
      <section className="py-20 bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="text-center mb-14">
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-slate-400 mb-3 block">{program.label}</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'var(--font-heading)' }}>{program.hero.title}</h2>
            <p className="text-slate-500 max-w-2xl mx-auto leading-relaxed">{program.hero.subtitle}</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-16">
            {program.highlights.map((h, i) => (
              <div key={h.label} className="bg-slate-50 border border-slate-200 rounded-2xl p-6 text-center hover:border-slate-300 transition-colors animate-fade-in-up" style={{ animationDelay: `${i * 0.05}s` }}>
                <div className="text-2xl md:text-3xl font-bold text-slate-900 mb-1" style={{ fontFamily: 'var(--font-heading)' }}>{h.value}</div>
                <div className="text-xs text-slate-500 font-semibold uppercase tracking-wider">{h.label}</div>
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            <div className="lg:col-span-5 bg-slate-50 border border-slate-200 rounded-2xl p-8">
              <h3 className="text-xl font-bold text-slate-900 mb-6" style={{ fontFamily: 'var(--font-heading)' }}>Program Details</h3>
              {program.details.map((d) => (
                <div key={d.label} className="flex justify-between items-center py-3.5 border-b border-slate-200 last:border-b-0">
                  <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">{d.label}</span>
                  <span className="text-sm font-bold text-slate-900 text-right">{d.value}</span>
                </div>
              ))}
            </div>

            <div className="lg:col-span-7 flex flex-col gap-6">
              <div className="bg-slate-900 text-white rounded-2xl p-8 flex-1">
                <h3 className="text-xl font-bold mb-6" style={{ fontFamily: 'var(--font-heading)' }}>Why Borrowers Choose This Program</h3>
                {program.benefits.map((b) => (
                  <div key={b} className="flex items-start gap-3 mb-4 last:mb-0">
                    <CheckCircle className={`h-5 w-5 ${colors.checkColor} flex-shrink-0 mt-0.5`} />
                    <span className="text-sm text-slate-300 leading-relaxed">{b}</span>
                  </div>
                ))}
              </div>
              <Link to="/apply" data-testid={`apply-${program.id}`} className={`${colors.button} text-white rounded-2xl p-8 flex items-center justify-between group transition-colors`}>
                <div>
                  <h3 className="text-lg font-bold mb-1" style={{ fontFamily: 'var(--font-heading)' }}>Apply for {program.label}</h3>
                  <p className="text-sm text-white/70">Get started in minutes</p>
                </div>
                <ArrowRight className="h-6 w-6 text-white group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Compare */}
      <section className="py-24 md:py-32 bg-[#FAFAFA]">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="text-center mb-14">
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#115E59] mb-3 block">Compare</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900" style={{ fontFamily: 'var(--font-heading)' }}>Which Program Fits You?</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {programs.map((p) => (
              <button key={p.id} onClick={() => { setActiveTab(p.id); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
                className="bento-card bg-white p-8 text-left group cursor-pointer">
                <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center mb-5 text-slate-600 group-hover:text-white group-hover:bg-slate-900 transition-all">{p.icon}</div>
                <h3 className="text-xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'var(--font-heading)' }}>{p.label}</h3>
                <p className="text-xs text-slate-400 font-semibold uppercase tracking-wider mb-4">{p.tagline}</p>
                {p.highlights.slice(0, 3).map((h) => (
                  <div key={h.label} className="flex justify-between text-sm mb-2">
                    <span className="text-slate-500">{h.label}</span>
                    <span className="font-bold text-slate-900">{h.value}</span>
                  </div>
                ))}
                <span className="mt-4 inline-block text-sm font-semibold text-[#115E59] group-hover:underline">View details &rarr;</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Use of Funds with images */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-8">
          <div className="text-center mb-14">
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#115E59] mb-3 block">Use of Funds</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900" style={{ fontFamily: 'var(--font-heading)' }}>Put Capital to Work</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: <RefreshCw className="h-5 w-5" />, title: 'Refinance Debt', desc: 'Replace expensive MCA or short term debt with predictable, long term payments.', color: 'bg-purple-50 text-purple-700' },
              { icon: <Building2 className="h-5 w-5" />, title: 'Business Expansion', desc: 'Open new locations, enter new markets, or scale existing operations.', color: 'bg-teal-50 text-teal-700' },
              { icon: <Wrench className="h-5 w-5" />, title: 'Equipment & Inventory', desc: 'Purchase machinery, technology, supplies, or stock up on inventory.', color: 'bg-amber-50 text-amber-700' },
              { icon: <DollarSign className="h-5 w-5" />, title: 'Working Capital', desc: 'Cover payroll, marketing, rent, and day to day operational costs.', color: 'bg-blue-50 text-blue-700' },
              { icon: <TrendingUp className="h-5 w-5" />, title: 'Growth Initiatives', desc: 'Invest in hiring, marketing campaigns, and revenue generating projects.', color: 'bg-rose-50 text-rose-700' },
              { icon: <Clock className="h-5 w-5" />, title: 'Seize Opportunities', desc: 'Move fast on time sensitive deals, contracts, or bulk purchasing discounts.', color: 'bg-indigo-50 text-indigo-700' },
            ].map((uc) => (
              <div key={uc.title} className="bento-card bg-[#FAFAFA] p-7">
                <div className={`w-10 h-10 rounded-xl ${uc.color} flex items-center justify-center mb-4`}>{uc.icon}</div>
                <h3 className="text-base font-bold text-slate-900 mb-2">{uc.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{uc.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA with image */}
      <section className="relative py-24 md:py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.unsplash.com/photo-1767150145058-367fb68021f2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBvZmZpY2UlMjBza3lsaW5lJTIwY2l0eXNjYXBlJTIwYnVzaW5lc3N8ZW58MHx8fHwxNzc0NTI0OTY0fDA&ixlib=rb-4.1.0&q=85" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-slate-950/85" />
        </div>
        <div className="max-w-7xl mx-auto px-6 md:px-8 text-center relative z-10">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6" style={{ fontFamily: 'var(--font-heading)' }}>Predictable Payments,<br />Real Growth</h2>
          <p className="text-lg text-slate-400 mb-10 max-w-xl mx-auto">Whether you need 3 years or 10, we'll match you with the right term loan for your business.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/apply" data-testid="term-loans-final-apply" className="inline-flex items-center gap-2 bg-teal-600 text-white font-semibold text-sm px-8 py-4 rounded-full hover:bg-teal-500 transition-colors">Apply for a Term Loan <ArrowRight className="h-4 w-4" /></Link>
            <Link to="/contact" className="inline-flex items-center gap-2 border border-white/20 text-white font-semibold text-sm px-8 py-4 rounded-full hover:bg-white/10 transition-colors">Contact Us</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TermLoans;
